import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ProductForm } from "@/components/portal/ProductForm";
import { api, errMsg, img, inr } from "@/lib/api";

const STATUS_CLS = { approved: "text-emerald-400", pending: "text-[#D4AF37]", rejected: "text-[#E06D3B]" };

export function ProductsTable({ products, isAdmin, reload }) {
  const [form, setForm] = useState(null);

  const toggleStock = async (p, v) => {
    try {
      await api.patch(`/portal/products/${p.id}`, { in_stock: v });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (p) => {
    const msg = isAdmin ? `Permanently delete "${p.name}"?` : `Mark "${p.name}" as out of stock?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/portal/products/${p.id}`);
      toast.success(isAdmin ? "Deleted" : "Marked out of stock");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="products-table">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl">{isAdmin ? "Catalogue" : "My sarees"} <span className="text-[#6E6558] text-sm">({products.length})</span></h2>
        <button onClick={() => setForm({})} className="btn-glow inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#0B0907]" data-testid="add-product-button">
          <Plus className="h-4 w-4" /> Add item
        </button>
      </div>
      <div className="space-y-2">
        {products.map((p) => (
          <div key={p.id} className="flex items-center gap-4 rounded-sm border border-[#D4AF37]/10 bg-[#14110E] p-3 hover:bg-[#1D1915] transition-colors" data-testid={`product-row-${p.id}`}>
            <img src={img(p.image_url)} alt="" className="h-14 w-12 object-cover rounded shrink-0 bg-[#0B0907]" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{p.name}</p>
              <p className="text-xs text-[#6E6558] truncate">
                {p.category === "apparel" ? "Other" : p.origin} · {p.sale_price ? <span className="text-[#E06D3B]">{inr(p.sale_price)} sale</span> : inr(p.price)}
                {isAdmin && p.created_by_name ? ` · by ${p.created_by_name}` : ""}
              </p>
            </div>
            <span className={`hidden sm:inline text-[10px] uppercase tracking-[0.15em] ${STATUS_CLS[p.status]}`} data-testid={`product-status-${p.id}`}>{p.status}</span>
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-[#A89F91]">
              <Switch checked={p.in_stock} onCheckedChange={(v) => toggleStock(p, v)} data-testid={`stock-toggle-${p.id}`} />
              <span className="hidden md:inline w-16">{p.in_stock ? "In stock" : "Out"}</span>
            </label>
            {isAdmin && <button onClick={() => setForm(p)} className="p-2 text-[#A89F91] hover:text-[#F3E5AB]" data-testid={`edit-product-${p.id}`} aria-label="Edit"><Pencil className="h-4 w-4" /></button>}
            <button onClick={() => remove(p)} className="p-2 text-[#A89F91] hover:text-[#E06D3B]" data-testid={`delete-product-${p.id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {products.length === 0 && <p className="text-[#6E6558] text-sm">No items yet.</p>}
      </div>
      {form && <ProductForm open onOpenChange={(o) => !o && setForm(null)} initial={form.id ? form : null} isAdmin={isAdmin} onSaved={reload} />}
    </div>
  );
}
